/**
 * Adaptive product count display
 *
 * Renders a collection's product count and keeps it in sync with the live
 * (filtered) count broadcast by the Athos collection app via the global
 * emitter. Lives outside the Preact app so it can be reused anywhere the
 * count needs to reflect active filters (e.g. the collection header).
 *
 * Until the app broadcasts a count it shows a pulsing skeleton, avoiding a
 * flash of the (potentially stale) server-rendered value.
 *
 * Attributes:
 * - data-format:   "parens" → "(1,234)" (default) | "label" → "1,234 Products".
 * - data-singular: Word for a single item in "label" format (default "Product").
 * - data-plural:   Word for multiple items in "label" format (default "Products").
 * - data-empty:    Text shown for zero items in "label" format (default "No Products").
 */

import { globalEmitter, emitterProps } from '../helpers/emitter-event'

declare global {
  interface Window {
    collectionProductCount?: number;
  }
}

type CountFormat = 'parens' | 'label'

export class ProductCountElement extends HTMLElement {
  private format: CountFormat = 'parens'
  private singular = 'Product'
  private plural = 'Products'
  private empty = 'No Products'

  private handleCount = (event: CustomEvent<number>) => {
    this.update(event.detail)
  }

  connectedCallback() {
    this.format = (this.getAttribute('data-format') as CountFormat) || 'parens'
    this.singular = this.getAttribute('data-singular') || this.singular
    this.plural = this.getAttribute('data-plural') || this.plural
    this.empty = this.getAttribute('data-empty') || this.empty

    const cached = window.collectionProductCount
    if (typeof cached === 'number') {
      this.update(cached)
    } else {
      this.renderSkeleton()
    }

    globalEmitter.on(emitterProps.collection.count, this.handleCount)
  }

  disconnectedCallback() {
    globalEmitter.off(emitterProps.collection.count, this.handleCount)
  }

  private renderSkeleton() {
    this.setAttribute('aria-busy', 'true')
    this.innerHTML =
      '<span class="block w-[25px] h-[21px] bg-page-secondary animate-pulse"></span>'
  }

  private update(count: number) {
    this.removeAttribute('aria-busy')
    this.textContent = this.formatCount(count)
  }

  private formatCount(count: number): string {
    const value = count.toLocaleString()

    if (this.format === 'label') {
      if (count === 0) return this.empty
      return `${value} ${count === 1 ? this.singular : this.plural}`
    }

    return `(${value})`
  }
}
